import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Loader, RadioGroup, RadioItem } from 'braid-design-system';
import type { FormEvent } from 'react';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { getSharedProfileTrackingStatus } from 'src/modules/analytics/profileAnalytics';
import {
  useGetSharedProfileVisibility,
  useUpdateSharedProfileVisibility,
} from 'src/modules/graphql/visibility/data';
import { useTypedSelector } from 'src/reducers';

import translations from './.vocab';

export const SharedProfileVisibility = () => {
  const { t } = useTranslations(translations);
  const { trackSharedProfileVisibilityUpdated } = useAnalytics();
  const { isShareableProfileActive, loading, error } =
    useGetSharedProfileVisibility();
  const { updateSharedProfileVisibility } = useUpdateSharedProfileVisibility();

  const { language } = useMelwaysInfo();
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);

  const onChangeFn = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => {
    updateSharedProfileVisibility({
      variables: {
        languageCode: language,
        zone,
        active: value === 'on',
      },
    });

    const sharedProfileTrackingStatus = getSharedProfileTrackingStatus({
      isShareableProfileActive: value === 'on',
      isShareableProfileCreated: true,
    });
    trackSharedProfileVisibilityUpdated({
      profileSharedStatus: sharedProfileTrackingStatus,
    });
  };

  return loading || error ? (
    <Loader size="small" />
  ) : (
    <RadioGroup
      id="sharedProfileOptions"
      label={t('Set visibility')}
      onChange={onChangeFn}
      value={isShareableProfileActive ? 'on' : 'off'}
      data={{ automation: 'shared-profile-visibility-options' }}
    >
      <RadioItem
        data={{ automation: 'on-radio-option' }}
        label={t('Shared Profile on')}
        description={t(
          'Your Shared Profile can be viewed, and reshared by others via your shared link.',
        )}
        value="on"
      />
      <RadioItem
        data={{ automation: 'off-radio-option' }}
        label={t('Shared Profile off')}
        description={t(
          'Your Shared Profile cannot be seen via your shared link.',
        )}
        value="off"
      />
    </RadioGroup>
  );
};
